//@flow
import React, { useRef, useEffect, useState } from 'react';
import _ from 'underscore';
import { useMediaQuery } from 'react-responsive';
import Link from './Link';
import {getImageUrl} from '../js/Stores/Mktg/mktgStoreClass';
import UrlData from '../js/Utils/url-data';

import type { BannerUiConfig } from '../js/Stores/Mktg/mktgModelInterfaces';

type Props = {
  items:BannerUiConfig[],
  switchTime?:number,
};

const HOST = UrlData.getFullHost();

const hasImage = (item:BannerUiConfig):boolean => {
  const image = item.imageSmall || item.imageMedium || item.imageLarge || item.imageXlarge || item.imageXXlarge;
  return Boolean(image);
};

const getFullLinkData = (path:string, external?:boolean) => {
  let url = path;
  let useAnchor = false;
  if (external || path.startsWith('http')) {
    useAnchor = true;
  }
  if (external && !path.startsWith('http')) {
    url = `${HOST}${path}`;
  }
  return {url, useAnchor};
};

const multiplyList = (list:any, count:number) => {
  let final:any = [];
  _.range(0, count).forEach(i => {
    final = final.concat(list);
  });
  return final;
};

const BannerSlider = (props:Props) => {
  const {
    items = [],
    switchTime = 7000,
  } = props;

  const isSmallScreen = useMediaQuery({ maxWidth: 440 });
  const isMediumScreen = useMediaQuery({ maxWidth: 640 });
  const isLargeScreen = useMediaQuery({ maxWidth: 840 });
  const isXLargeScreen = useMediaQuery({ maxWidth: 1280 });
  const isXXLargeScreen = useMediaQuery({ minWidth: 1281 });

  const allItems = multiplyList(items, 8);
  const listElement = useRef<any>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [started, setStarted] = useState(false);
  const [stopped, setStopped] = useState(false);
  const switchInterval = useRef<any>(null);

  const startInterval = (activeIndex:number) => {
    switchInterval.current = setInterval(() => {
      const nextIndex = activeIndex === allItems.length - 1
        ? 0
        : activeIndex + 1;
      setActiveIndex(nextIndex);
      handleScroll(nextIndex);
    }, switchTime);
  };

  useEffect(() => {
    if (!stopped) startInterval(activeIndex);
    return () => {
      if (switchInterval.current) clearInterval(switchInterval.current);
    };
  }, [activeIndex, stopped]);

  const handleScroll = (index:number, preventReset?:boolean) => {
    const listElementRef = listElement.current;
    if (index === 0 && started && !preventReset && listElementRef) {
      listElement.current.scrollLeft = 0;
    }
    else {
      if (!started) setStarted(true);
      if (!listElementRef) return;
      const width = listElementRef.clientWidth;
      const nextLeft = index * (width || 0);
      listElementRef.scrollTo({
        top: 0,
        left: nextLeft,
        behavior: 'smooth',
      });
    }
  };

  // Get nearest index relative to entire list
  const getClickedIndex = (index:number, activeIndex:number, groupCount:number) => {
    const multiplier = Math.floor(activeIndex / groupCount);
    const next = (multiplier * groupCount) + index;
    return next;
  };

  const handleClickDot = (index:number) => () => {
    if (switchInterval.current) clearInterval(switchInterval.current);
    setStopped(true);
    const nextIndex = getClickedIndex(index, activeIndex, items.length);
    setActiveIndex(nextIndex);
    handleScroll(nextIndex, true);
    startInterval(nextIndex);
  };

  const getIsDotActive = (index:number) => {
    const groupCount = items.length;
    return index === activeIndex || activeIndex % groupCount === index;
  };

  const getImage = (item:BannerUiConfig) => {
    // TODO: see if there is a more elegant way of
    // getting image
    let image = item.imageLarge || item.imageXlarge || item.imageMedium || item.imageSmall || item.imageXXlarge;
    if (isSmallScreen) {
      image = item.imageSmall || item.imageMedium || item.imageLarge || item.imageXlarge || item.imageXXlarge;
    }
    else if (isMediumScreen) {
      image = item.imageMedium || item.imageSmall || item.imageLarge || item.imageXlarge || item.imageXXlarge;
    }
    else if (isLargeScreen) {
      image = item.imageLarge || item.imageXlarge || item.imageXXlarge || item.imageMedium || item.imageSmall;
    }
    else if (isXLargeScreen) {
      image = item.imageXlarge || item.imageLarge || item.imageXXlarge || item.imageMedium || item.imageSmall;
    }
    else if (isXXLargeScreen) {
      image = item.imageXXlarge || item.imageXlarge || item.imageLarge || item.imageMedium || item.imageSmall;
    }
    return getImageUrl(image);
  };

  const renderItem = (item:BannerUiConfig, index:number) => {
    const content = (
      <div className="content">
        {hasImage(item) && (
          <img src={getImage(item)} alt={item.title || "NatureBox"}></img>
        )}
        <div className="content-inner">
          {item.title && <h2 className="banner-title">{item.title}</h2>}
          {item.text1 && <p className="banner-text-1">{item.text1}</p>}
          {item.text2 && <p className="banner-text-2">{item.text2}</p>}
          {item.text3 && <p className="banner-text-3">{item.text3}</p>}
          {item.text4 && <p className="banner-text-4">{item.text4}</p>}
        </div>
      </div>
    );
    if (item.link) {
      const {url} = getFullLinkData(item.link, item.isExternalLink);
      return (
        <li key={index} className="item">
          <Link to={url} external={item.isExternalLink}>
            {content}
          </Link>
        </li>
      );
    }
    else {
      return (
        <li key={index} className="item">
          {content}
        </li>
      );
    }
  };

  return (
    <div className="banner-slider">
      <ul ref={listElement} className="items-container">
        {allItems.map((item, index) => (
          renderItem(item, index)
        ))}
      </ul>
      <ul className="paging-dots">
        {_.range(0, items.length).map(index => (
          <li key={index} className={`${getIsDotActive(index) ? 'active' : ''}`}>
            <button onClick={handleClickDot(index)}></button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default BannerSlider;
